import { type CSSProperties, type FC, type MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import "./ProductComponentsTable.scss";
import {
	Badge,
	Button,
	Content,
	ContentVariants,
	EmptyState,
	Icon,
	Menu,
	MenuContent,
	MenuItem,
	MenuList,
	MenuToggle,
	Popper,
	SearchInput,
	Stack,
	StackItem,
	ToggleGroup,
	ToggleGroupItem,
	ToolbarFilter,
	ToolbarItem,
} from "@patternfly/react-core";
import { Modal, ModalVariant } from "@patternfly/react-core/deprecated";
import {
	CheckCircleIcon,
	CubesIcon,
	ExclamationCircleIcon,
	FilterIcon,
	SearchIcon,
	SecurityIcon,
} from "@patternfly/react-icons";
import { TableText } from "@patternfly/react-table";
import { ErrorPopup, SuccessPopup, useAlertContext } from "@rhc-shared-components/alert-component";
import { useControllerContext } from "@rhc-shared-components/control-wrapper";
import { TableToolbar } from "@rhc-shared-components/table-toolbar";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow, parseISO } from "date-fns";
import { syncTaticIds } from "../../api/pfs";
import { patchCertificationProject, patchProductListing } from "../../api/pyxis";
import { ProductKeys } from "../../constants/product";
import {
	DepreciatedProjectTypes,
	ECatalogVisibility,
	getProjectTypeData,
	ProjectKeys,
	ProjectTypes,
} from "../../constants/project";
import { CertificationStatus } from "../../constants/projectTypes";
import { type TProductDetails, useProductMetadata } from "../../hooks/useProductMetadata";
import type { ProjectListGraphQLResponseDataEntity } from "../../interfaces/graphql";
import { invalidateProductQueries, productDetailQueryKey, useProductDetail } from "../../queries/productDetails";
import type { CertificationProject } from "../../types/generated-graphql";
import Env from "../../utils/env";
import { GetPublishedStatus } from "../../utils/getPublishedStatus";
import { Table } from "../general/Table";
import { type IColumn, PFTableV2 } from "../general/Table/PFTableV2";
import { TableContextConsumer } from "../general/Table/TableContext";
import { ComponentCreateModal } from "./CreateComponentsModal/CreateComponentModal";
import { ProductComponentsRequirements } from "./ProductComponentsRequirements";
import { ValidatedProjectTypePopup } from "./shared/ValidatedProjectTypePopup";

type TProjectStatus = Set<"Active" | "Archived">;

interface ProductComponentsTableProps {
	title?: string | null;
	hideActiveFilter?: boolean;
	isMiniTable?: boolean;
	hideAddComponentButton?: boolean;
	hideContextMenu?: boolean;
	switchableTypeColumn?: "componentType" | "certificationStatus";
	hideValidatedComponents?: boolean;
}

const ProductComponentsTable: FC<ProductComponentsTableProps> = ({
	title = "Attached Components",
	hideActiveFilter = false,
	isMiniTable = false,
	hideAddComponentButton = false,
	hideContextMenu = false,
	switchableTypeColumn = "componentType",
	hideValidatedComponents = false,
}) => {
	const {
		productDetail,
		certificationListState,
		setCertificationListState,
		globalDisabledState,
		activeCertificationListState,
	} = useProductMetadata();
	const { isReadOnly } = useControllerContext();
	const [projectList, setProjectList] = useState<ProjectListGraphQLResponseDataEntity[]>([]);
	// Select Logic: https://www.patternfly.org/v4/demos/filters/#checkbox-select
	const [selectedCertificationStatuses, setSelectedCertificationStatuses] = useState<string[]>([]);
	const [searchValue, setSearchValue] = useState("");
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const menuRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const toggleRef = useRef<HTMLButtonElement>(null);
	const emptyModalState = {
		display: false,
		okCallback: () => {},
	};
	const [selectedProjectStatus, setSelectedProjectStatus] = useState<TProjectStatus>(
		new Set(["Active"]) as TProjectStatus,
	);
	const projectListColumns: IColumn[] = [
		{
			title: "Name",
			cell: (data: ProjectListGraphQLResponseDataEntity) => (
				<>
					{data[ProjectKeys.projectStatus] === "archived" ||
					DepreciatedProjectTypes.includes(data[ProjectKeys.type]) ? (
						<TableText wrapModifier="breakWord">
							<Content>
								<Content component={ContentVariants.p}>
									{data[ProjectKeys.projectName]}{" "}
									{data[ProjectKeys.projectStatus] === "archived" && <Badge isRead>Archived</Badge>}
									{data?.[ProjectKeys.catalogVisibility] === ECatalogVisibility.hidden && <Badge isRead>Private</Badge>}
								</Content>
							</Content>
						</TableText>
					) : (
						<TableText wrapModifier="breakWord">
							<a href={`${Env.projectPrefix}/${productDetail[ProductKeys.id]}/${data[ProjectKeys.id]}`}>
								{data[ProjectKeys.projectName]}
								{data?.[ProjectKeys.catalogVisibility] === ECatalogVisibility.hidden && <Badge isRead>Private</Badge>}
							</a>
						</TableText>
					)}
				</>
			),
			extraProps: { column: { width: isMiniTable ? 40 : 30 } },
			sortable: false,
			accessor: (data: ProjectListGraphQLResponseDataEntity) => data[ProjectKeys.projectName],
			key: ProjectKeys.projectName,
		},
		...(!isMiniTable
			? [
					{
						title: "Certification",
						cell: (data: ProjectListGraphQLResponseDataEntity) => (
							<TableText wrapModifier="breakWord">{data[ProjectKeys.certificationStatus]}</TableText>
						),
						extraProps: { column: { width: 15 } },
						sortable: false,
						accessor: (data: ProjectListGraphQLResponseDataEntity) => data[ProjectKeys.projectName],
						key: ProjectKeys.certificationStatus,
					},
					{
						title: "Security",
						extraProps: { column: { width: 15 } },
						sortable: false,
						accessor: (data: ProjectListGraphQLResponseDataEntity) => {
							const imageId = data?.edges?.images?.data?.[0]?.[ProjectKeys.id];
							const vulnerabilitiesCount = data?.edges?.images?.data?.[0]?.edges?.vulnerabilities?.total || 0;
							const repoGradeData = data?.edges?.images?.data?.[0]?.freshness_grades || [];
							const recentGrade = repoGradeData?.find(
								(grade) =>
									new Date() > new Date(grade.start_date || "") &&
									(grade.end_date ? new Date() < new Date(grade.end_date) : true),
							);
							const hasHealthIndex =
								["container", "scratch"].includes(data?.container?.type || "") ||
								["OpenStack-infra-container", "OpenStack-app-container"].includes(data?.type) ||
								(data?.type === "RHEL" && data?.container?.type === "container");
							if (hasHealthIndex) {
								return recentGrade?.grade ? (
									<>
										<pfe-health-index health-index={recentGrade?.grade} size="mini">
											{recentGrade?.grade || "unknown"}
										</pfe-health-index>
										&ensp;
										{vulnerabilitiesCount > 0 && (
											<a
												href={
													Env.ConnectPortalUrls[Env.getEnvName()] +
													`projects/${productDetail[ProductKeys.id]}/${data?.[ProjectKeys.id]}/security/vulnerabilities/${imageId}`
												}
												rel="noreferrer"
												target="_blank"
												style={{ verticalAlign: "middle" }}
											>
												<SecurityIcon color="#ec7a08" />
												&nbsp;Vulnerable
											</a>
										)}
									</>
								) : (
									"Unknown"
								);
							} else {
								return "N/A";
							}
						},
						key: "security",
					},
				]
			: []),
		...(switchableTypeColumn === "componentType"
			? [
					{
						title: "Type",
						extraProps: { column: { width: isMiniTable ? 40 : 20 } },
						sortable: true,
						accessor: (data: ProjectListGraphQLResponseDataEntity) => {
							const type = getProjectTypeData(data as unknown as CertificationProject);
							if (data?.[ProjectKeys.type] === ProjectTypes.openShiftCnf) {
								if (data?.[ProjectKeys.certificationLevel] === "Vendor validated") {
									return <ValidatedProjectTypePopup text={"Validated Cloud-Native Function (CNF)"} />;
								} else if (data?.[ProjectKeys.certificationLevel] === "Certified") {
									return "Certified Cloud-Native Function (CNF)";
								}
							}
							if (data?.[ProjectKeys.type] === ProjectTypes.openstackVNF) {
								if (data?.[ProjectKeys.certificationLevel] === "Vendor validated") {
									return <ValidatedProjectTypePopup text={"Validated Openstack VNF"} />;
								} else if (data?.[ProjectKeys.certificationLevel] === "Certified") {
									return "Certified Openstack VNF";
								}
							}
							return type;
						},
						key: "projectType",
					},
				]
			: [
					{
						title: "Certification Status",
						cell: (data: ProjectListGraphQLResponseDataEntity) => (
							<TableText wrapModifier="breakWord">
								{data[ProjectKeys.certificationStatus] === "Published" ? (
									<>
										<Icon isInline={true} status="success">
											<CheckCircleIcon />
										</Icon>
										&nbsp;&nbsp;
										{data[ProjectKeys.certificationLevel] === "Vendor validated" ? "Vendor validated" : "Certified"}
									</>
								) : (data?.edges?.images?.data?.[0]?.edges?.test_results?.data?.[0]?.results?.failed?.length ?? 0) >
									0 ? (
									<a
										href={`${Env.projectPrefix}/${productDetail[ProductKeys.id]}/${
											data[ProjectKeys.id]
										}/certification/test-results/${
											data?.edges?.images?.data?.[0]?.edges?.test_results?.data?.[0]?._id
										}`}
									>
										<Icon isInline={true} status="danger">
											<ExclamationCircleIcon />
										</Icon>
										&nbsp;&nbsp;Not certified
									</a>
								) : (
									"Not certified"
								)}
							</TableText>
						),
						extraProps: { column: { width: isMiniTable ? 40 : 20 } },
						sortable: false,
						accessor: (data: ProjectListGraphQLResponseDataEntity) => data[ProjectKeys.projectName],
						key: ProjectKeys.certificationStatus,
					},
				]),
		{
			title: "Created",
			extraProps: { column: { width: 20 } },
			sortable: false,
			cell: (data) => `${formatDistanceToNow(parseISO(data[ProjectKeys.creationDate]))} ago`,
			accessor: (data) => data[ProjectKeys.creationDate],
			key: ProjectKeys.creationDate,
		},
	];
	const [modalState, setModalState] = useState(emptyModalState);
	const closeModal = () => {
		setModalState(emptyModalState);
	};
	const queryClient = useQueryClient();

	const [showCreateComponentModal, setCreateComponentModal] = useState(false);
	const handleCreateModal = (data: boolean) => {
		setCreateComponentModal(data);
	};

	const patchProjectMutation = useMutation<ProjectListGraphQLResponseDataEntity, Error, Record<string, string>>({
		mutationFn: (request) => patchCertificationProject(request) as Promise<ProjectListGraphQLResponseDataEntity>,
		onError: () => {
			ErrorPopup(setAlertState);
		},
	});
	const patchProductMutation = useMutation<string, Error, ProjectListGraphQLResponseDataEntity>({
		mutationFn: async (rowData) => {
			const requestData: Partial<TProductDetails> = {
				[ProductKeys.id]: productDetail[ProductKeys.id],
				[ProductKeys.certProjects]: productDetail[ProductKeys.certProjects]?.filter(
					(item) => item !== rowData[ProjectKeys.id],
				) as string[],
			};
			await patchProductListing(requestData);
			return rowData[ProjectKeys.projectName];
		},
		onError: () => {
			ErrorPopup(setAlertState);
		},
		onSuccess: (data) => {
			SuccessPopup(setAlertState, `Removed ${data} successfully`);
		},
	});
	const { isFetching: isProductDetailFetching } = useProductDetail(productDetail?.[ProductKeys.id]);
	const isApiLoading = patchProjectMutation.isPending || isProductDetailFetching;
	const { setAlertState } = useAlertContext();
	const [currentPage, setSelectedPage] = useState<number>(1);
	const [perPage, setPerPage] = useState<number>(10);
	const onToggleClick = (ev: MouseEvent) => {
		ev.stopPropagation(); // Stop handleClickOutside from handling
		setTimeout(() => {
			if (menuRef.current) {
				const firstElement = menuRef.current.querySelector("li > button:not(:disabled)");
				firstElement && (firstElement as HTMLElement).focus();
			}
		}, 0);
		setIsOpen(!isOpen);
	};

	const filterStatusHandler = (
		event: globalThis.MouseEvent | React.MouseEvent<any, globalThis.MouseEvent> | React.KeyboardEvent<Element>,
		_selected?: boolean,
	) => {
		const id = (event.currentTarget as HTMLElement).id as "Active" | "Archived";
		const newStatus = new Set(selectedProjectStatus);
		newStatus.has(id) ? newStatus.delete(id) : newStatus.add(id);
		if (newStatus.size < 1) {
			return;
		}
		setSelectedProjectStatus(newStatus);
		onFilterChange("statusFilter", newStatus);
	};
	const getFilteredList = useCallback(
		(
			list: ProjectListGraphQLResponseDataEntity[],
			searchFilterValue: string,
			certificationStatusFilterValues: string[],
			statusFilterValue: TProjectStatus,
		): ProjectListGraphQLResponseDataEntity[] => {
			list =
				certificationStatusFilterValues?.length > 0
					? list.filter((item) => certificationStatusFilterValues.includes(item[ProjectKeys.certificationStatus]))
					: list;
			list =
				searchFilterValue?.length > 0
					? list.filter((item) =>
							item[ProjectKeys.projectName]?.toLowerCase().includes(searchFilterValue?.toLowerCase()),
						)
					: list;
			if (statusFilterValue.has("Active") && !statusFilterValue.has("Archived")) {
				list = list.filter((item) => item[ProjectKeys.projectStatus] !== "archived");
			} else if (!statusFilterValue.has("Active") && statusFilterValue.has("Archived")) {
				list = list.filter((item) => item[ProjectKeys.projectStatus] !== "active");
			}
			return list;
		},
		[],
	);
	const onFilterChange = useCallback(
		(type: "search" | "chip" | "resetChip" | "statusFilter", value?: string | TProjectStatus) => {
			const itemStr = value?.toString() || "";
			const newStatusFilter: TProjectStatus =
				type === "statusFilter" ? (value as TProjectStatus) : selectedProjectStatus;
			let newFilteredList = [...certificationListState];
			let newCertificationStatuses: string[] =
				type === "chip"
					? selectedCertificationStatuses.includes(itemStr)
						? selectedCertificationStatuses.filter((selection) => selection !== itemStr)
						: [itemStr, ...selectedCertificationStatuses]
					: selectedCertificationStatuses;
			newCertificationStatuses = type === "resetChip" ? [] : newCertificationStatuses; // Reset chip
			const newSearchValue = type === "search" ? (value as string) : searchValue;
			if (type === "chip" || type === "resetChip") {
				setSelectedCertificationStatuses(newCertificationStatuses);
			}
			if (type === "search") {
				setSearchValue(value as string);
			}
			newFilteredList = getFilteredList(newFilteredList, newSearchValue, newCertificationStatuses, newStatusFilter);
			setSelectedPage(1);
			setProjectList(newFilteredList);
		},
		[selectedProjectStatus, certificationListState, selectedCertificationStatuses, searchValue, getFilteredList],
	);
	const MenuComponent = (
		<Menu
			ref={menuRef}
			id="checkbox-select-menu"
			onSelect={(_event, itemId) => {
				onFilterChange("chip", itemId as string);
			}}
			selected={selectedCertificationStatuses}
		>
			<MenuContent>
				<MenuList>
					{CertificationStatus.sort((a, b) => a.text.localeCompare(b.text)).map((elm) => {
						return (
							<MenuItem
								isSelected={selectedCertificationStatuses.includes(elm.text)}
								hasCheckbox
								key={elm.text}
								itemId={elm.text}
							>
								{elm.text}
							</MenuItem>
						);
					})}
				</MenuList>
			</MenuContent>
		</Menu>
	);
	const Toggle = (
		<MenuToggle
			isDisabled={isApiLoading}
			ref={toggleRef}
			onClick={onToggleClick}
			isExpanded={isOpen}
			icon={<FilterIcon />}
			{...(selectedCertificationStatuses.length > 0 && {
				badge: <Badge isRead>{selectedCertificationStatuses.length}</Badge>,
			})}
			style={
				{
					width: "200px",
				} as CSSProperties
			}
		>
			Filter
		</MenuToggle>
	);
	const handleMenuKeys = useCallback(
		(event: KeyboardEvent) => {
			if (isOpen && menuRef.current?.contains(event.target as Node)) {
				if (event.key === "Escape" || event.key === "Tab") {
					setIsOpen(!isOpen);
					toggleRef.current?.focus();
				}
			}
		},
		[isOpen],
	);

	const handleClickOutside = useCallback(
		(event: globalThis.MouseEvent) => {
			if (isOpen && !menuRef.current?.contains(event.target as Node)) {
				setIsOpen(false);
			}
		},
		[isOpen],
	);

	useEffect(() => {
		window.addEventListener("keydown", handleMenuKeys);
		window.addEventListener("click", handleClickOutside);
		return () => {
			window.removeEventListener("keydown", handleMenuKeys);
			window.removeEventListener("click", handleClickOutside);
		};
		// eslint-disable-next-line
	}, [handleClickOutside, handleMenuKeys]);

	// End: Select Logic
	useEffect(() => {
		onFilterChange("statusFilter", selectedProjectStatus);
		// eslint-disable-next-line
	}, [onFilterChange, selectedProjectStatus]);
	return (
		<Stack hasGutter={true}>
			{title && (
				<StackItem>
					<Content component="h2">{title}</Content>
				</StackItem>
			)}
			<StackItem>
				<Table
					disableBuiltInPagination={true}
					columns={projectListColumns}
					rows={projectList?.slice((currentPage - 1) * perPage, currentPage * perPage) || []}
					{...(!hideContextMenu && {
						actionResolver: (rowData: ProjectListGraphQLResponseDataEntity) => {
							const filterCheckAndResetPage = (newCertificationList: ProjectListGraphQLResponseDataEntity[]) => {
								newCertificationList = getFilteredList(
									certificationListState,
									searchValue,
									selectedCertificationStatuses,
									selectedProjectStatus,
								);
								if (newCertificationList?.length === perPage) {
									setSelectedPage(1);
								}
							};
							const archiveRowActions =
								rowData && rowData[ProjectKeys.projectStatus].toLowerCase() === "archived"
									? {
											title: "Restore",
											isDisabled: isReadOnly || isApiLoading,
											onClick: async () => {
												const requestData = {
													[ProjectKeys.id]: rowData[ProjectKeys.id],
													[ProjectKeys.projectStatus]: "active",
												};
												try {
													const response = await patchProjectMutation.mutateAsync(requestData);
													const newProjectList = certificationListState.map((item) => {
														if (rowData[ProjectKeys.id] === item[ProjectKeys.id]) {
															item = {
																...item,
																...response,
															};
														}
														return item;
													});
													filterCheckAndResetPage(certificationListState);
													setCertificationListState(newProjectList);
													invalidateProductQueries(queryClient, productDetail[ProductKeys.id]);
													SuccessPopup(
														setAlertState,
														`Restored ${response[ProjectKeys.projectName]} project successfully`,
													);
												} catch (_e) {
													ErrorPopup(setAlertState);
												}
											},
										}
									: {
											title: "Archive",
											tooltip:
												GetPublishedStatus(rowData) === "Published" ? "Published components can't be archived" : null,
											className: GetPublishedStatus(rowData) === "Published" ? "enable-pointer-events" : null,
											isDisabled: GetPublishedStatus(rowData) === "Published" || isReadOnly || isApiLoading,
											onClick: async () => {
												const requestData = {
													[ProjectKeys.id]: rowData[ProjectKeys.id],
													[ProjectKeys.projectStatus]: "archived",
												};
												try {
													const response = await patchProjectMutation.mutateAsync(requestData);
													syncTaticIds(rowData[ProjectKeys.id], "CERTIFICATION_STATUS_ARCHIVED");
													const newProjectList = certificationListState.map((item) => {
														if (rowData[ProjectKeys.id] === item[ProjectKeys.id]) {
															item = {
																...item,
																...response,
															};
														}
														return item;
													});
													filterCheckAndResetPage(newProjectList);
													setCertificationListState(newProjectList);
													invalidateProductQueries(queryClient, productDetail[ProductKeys.id]);
													SuccessPopup(
														setAlertState,
														`Archived ${response[ProjectKeys.projectName]} project successfully`,
													);
												} catch (_e) {
													ErrorPopup(setAlertState);
												}
											},
										};
							// for containers and operators
							const privateCertOptions =
								rowData?.[ProductKeys.type] === ProjectTypes.containers
									? [
											{
												title: `Make ${
													rowData?.[ProjectKeys.catalogVisibility] === ECatalogVisibility.hidden ? "public" : "private"
												}`,
												isDisabled:
													isReadOnly ||
													isApiLoading ||
													rowData?.[ProjectKeys.projectStatus].toLowerCase() === "archived",
												onClick: async () => {
													const requestData = {
														[ProjectKeys.id]: rowData[ProjectKeys.id],
														[ProjectKeys.catalogVisibility]: `${rowData?.[ProjectKeys.catalogVisibility] === ECatalogVisibility.hidden ? ECatalogVisibility.visible : ECatalogVisibility.hidden}`,
													};
													try {
														const response = await patchProjectMutation.mutateAsync(requestData);
														const newProjectList = certificationListState.map((item) => {
															if (rowData[ProjectKeys.id] === item[ProjectKeys.id]) {
																item = {
																	...item,
																	...response,
																};
															}
															return item;
														});
														filterCheckAndResetPage(newProjectList);
														setCertificationListState(newProjectList);
														SuccessPopup(setAlertState, `Component visibility updated successfully`);
													} catch (_e) {
														ErrorPopup(setAlertState);
													}
												},
											},
										]
									: [];
							return [
								archiveRowActions,
								{
									title: "Remove",
									isDisabled: productDetail?.[ProductKeys.isPublished] || isReadOnly || isApiLoading,
									className: productDetail?.[ProductKeys.isPublished] ? "enable-pointer-events" : null,
									tooltip: productDetail?.[ProductKeys.isPublished]
										? "Components attached to published products cant be removed. Un-publish the product first."
										: null,
									onClick: async () => {
										setModalState({
											display: true,
											okCallback: async () => {
												await patchProductMutation.mutateAsync(rowData, {
													onSuccess: () => {
														const newCertificationList = certificationListState?.filter(
															(item) => item[ProjectKeys.id] !== rowData[ProjectKeys.id],
														);
														filterCheckAndResetPage(newCertificationList);
														setCertificationListState(newCertificationList);
														queryClient.invalidateQueries({
															queryKey: productDetailQueryKey(productDetail[ProductKeys.id]),
														});
														invalidateProductQueries(queryClient, productDetail[ProductKeys.id]);
														setModalState(emptyModalState);
													},
												});
											},
										});
									},
								},
								...privateCertOptions,
							];
						},
					})}
				>
					<TableContextConsumer>
						{() => {
							return (
								<TableToolbar
									pageState={{ page: currentPage, perPage: perPage }}
									onPageUpdate={(newPageNum: number, newPerPage: number) => {
										setSelectedPage(newPageNum);
										setPerPage(newPerPage);
									}}
									extraPaginationProps={{
										isCompact: true,
										isDisabled: isApiLoading,
									}}
									onClearAllFilters={() => {
										onFilterChange("resetChip");
									}}
									showBottomPagination={false}
									totalItems={projectList.length <= 10 ? 0 : projectList.length}
									customToolbarItems={[
										<ToolbarFilter
											key={0}
											labels={selectedCertificationStatuses}
											deleteLabel={(_, chip) => {
												onFilterChange("chip", chip as string);
											}}
											deleteLabelGroup={() => {
												onFilterChange("resetChip");
											}}
											categoryName={"Status"}
										>
											<div ref={containerRef}>
												<Popper
													trigger={Toggle}
													popper={MenuComponent}
													{...(containerRef.current ? { appendTo: containerRef.current } : {})}
													isVisible={isOpen}
												/>
											</div>
										</ToolbarFilter>,
										<ToolbarItem key={2}>
											<SearchInput
												isDisabled={isApiLoading}
												value={searchValue}
												placeholder={"Search by component name"}
												style={{
													minWidth: `15em`,
												}}
												onClear={() => {
													onFilterChange("search", "");
												}}
												onChange={(_, value) => {
													onFilterChange("search", value);
												}}
											/>
										</ToolbarItem>,
										...(!hideAddComponentButton
											? [
													<ToolbarItem key={4}>
														<Button
															onClick={() => {
																setCreateComponentModal(true);
															}}
															isDisabled={isReadOnly || isApiLoading || globalDisabledState}
														>
															Add component
														</Button>
													</ToolbarItem>,
												]
											: []),
										...(!hideActiveFilter
											? [
													<ToolbarItem key={5}>
														<ToggleGroup>
															<ToggleGroupItem
																text="Active"
																key={0}
																buttonId="Active"
																isSelected={selectedProjectStatus.has("Active")}
																onChange={filterStatusHandler}
															/>
															<ToggleGroupItem
																text="Archived"
																buttonId="Archived"
																key={1}
																isSelected={selectedProjectStatus.has("Archived")}
																onChange={filterStatusHandler}
															/>
														</ToggleGroup>
													</ToolbarItem>,
												]
											: []),
									]}
								>
									<PFTableV2
										aria="Attached Project Listings"
										emptyStateComponent={
											searchValue || selectedCertificationStatuses?.length > 0 ? null : (
												<EmptyState
													headingLevel="h4"
													icon={isMiniTable ? CubesIcon : SearchIcon}
													titleText={isMiniTable ? "Add a component to get started" : "No results found"}
												></EmptyState>
											)
										}
									/>
									{activeCertificationListState?.length > 0 && !isMiniTable && <ProductComponentsRequirements />}
								</TableToolbar>
							);
						}}
					</TableContextConsumer>
				</Table>
			</StackItem>
			<Modal
				variant={ModalVariant.small}
				isOpen={modalState.display}
				title={""}
				aria-label={"Delete project modal"}
				className={"modal-style"}
				onClose={closeModal}
				appendTo={document.getElementById("modal-div-container") || document.body}
				actions={[
					<Button
						isLoading={isApiLoading || patchProductMutation.isPending}
						key="confirm"
						variant="danger"
						onClick={modalState.okCallback}
						data-testid="shared-modal-ok"
						isDisabled={isReadOnly || isApiLoading || patchProductMutation.isPending}
					>
						Remove
					</Button>,
					<Button
						isDisabled={isApiLoading || patchProductMutation.isPending}
						key="cancel"
						variant="link"
						onClick={closeModal}
						data-testid="shared-modal-cancel"
					>
						Cancel
					</Button>,
				]}
			>
				<div>Removing a component will detach it from this product listing. Do you want to remove it?</div>
			</Modal>
			<ComponentCreateModal
				hideValidatedComponents={hideValidatedComponents}
				isVisible={showCreateComponentModal}
				handleCreateModal={handleCreateModal}
			/>
		</Stack>
	);
};

export default ProductComponentsTable;
